import type { GmapsAddress } from '/imports/gmaps';

/**
 * The one canonical full-address string, derived from a GmapsAddress's structured components rather than
 * any provider's raw `formatted_address` (the client Places SDK and the server Geocoding API format that
 * string differently). US-postal style: "1274 Auto Park Way, Escondido, CA 92029" — street parts and city
 * comma-separated, state and zip space-joined, country appended only for non-US addresses.
 *
 * Type-only import of GmapsAddress keeps this module dependency-free so both client and server can use it.
 * @param a - the structured address (any subset of the fields)
 * @returns the canonical formatted string
 */
export const formatGmapsAddress = (a: Partial<GmapsAddress>): string => {
  const trim = (s?: string) => (s || '').trim();
  // `line` is the reliably-populated street on every source (client sets "number route", the server sets
  // addressLines[0], legacy rows carry the full street) — so prefer it, and only fall back to number+route
  // when line is missing or malformed. `line2` (suite/unit) is appended when present.
  let streetMain = trim(a.line);
  if (!streetMain || streetMain.includes('undefined')) streetMain = [a.number, a.route].filter(Boolean).join(' ').trim();
  const street = [streetMain, trim(a.line2)].filter(Boolean).join(', ');
  const stateZip = [trim(a.state), trim(a.zipcode)].filter(Boolean).join(' ');
  const cityStateZip = [trim(a.city), stateZip].filter(Boolean).join(', ');
  let formatted = [street, cityStateZip].filter(Boolean).join(', ');
  const isUs = !a.country || a.country === 'US' || a.country === 'USA' || a.countryName === 'United States';
  if (!isUs && (a.countryName || a.country)) formatted += `, ${a.countryName || a.country}`;
  return formatted;
};
